/** Sangtekst som data: seksjoner, akkordlinjer, tekst med akkorder. Ren tekstbehandling. Tekst forsvinner aldri. */
import { erBokstavakkord, erTallakkord, tolkAkkord, type Akkord, type Rot } from './akkord';
import { erTallakkordlinje, harTallakkorder, SEKSJONSORD } from './tallfil';

export interface LinjeAkkord {
  tekst: string;
  akkord: Akkord | null;
  kolonne: number;
}
export type SangLinje =
  | { type: 'tom' }
  | { type: 'seksjon'; tekst: string }
  | { type: 'tekst'; tekst: string; akkorder: LinjeAkkord[] };
export interface SangDokument {
  notasjon: 'bokstav' | 'tall';
  brukerH: boolean;
  linjer: SangLinje[];
}

type Notasjon = SangDokument['notasjon'];
/** En linje før parring: en akkordlinje vet ennå ikke om den får tekst under seg. */
type Rad = SangLinje | { type: 'akkorder'; akkorder: LinjeAkkord[] };

const SEKSJON = new RegExp(`^(?:${SEKSJONSORD})(?:\\s*\\d+)?(?:\\s+(?:x\\d+|\\(x\\d+\\)))?\\s*:?$`, 'i');
const LEDETEKST = new RegExp(`^(\\s*)((?:${SEKSJONSORD})\\s*\\d*\\s*:)`, 'i');
const HAKE_ALENE = /^\s*\[([^[\]]+)\]\s*$/;
const I_HAKE = /\[([^[\]]+)\]/g;
const MERKE = /^(?:\||-|\/{1,3}|x\d+|\(x\d+\))$/;
const MINSTE_LINJER_DOBBEL = 4;
const BOKSTAV_ELLER_TALL = new RegExp('[\\p{L}\\p{N}]', 'u');

const delIOrd = (tekst: string): string[] => tekst.trim().split(/\s+/);
const akkordtest = (notasjon: Notasjon): ((token: string) => boolean) => (notasjon === 'tall' ? erTallakkord : erBokstavakkord);

function lagAkkord(token: string, kolonne: number): LinjeAkkord {
  return { tekst: token.replace(/♯/g, '#').replace(/♭/g, 'b'), akkord: tolkAkkord(token), kolonne };
}

/** `[Hva som helst]` alene, med minst én bokstav eller ett tall og uten å være akkorder, eller et ord fra fast liste. */
export function erSeksjon(linje: string): boolean {
  const hake = HAKE_ALENE.exec(linje);
  if (hake) return BOKSTAV_ELLER_TALL.test(hake[1]) && !delIOrd(hake[1]).every(erBokstavakkord);
  return SEKSJON.test(linje.trim());
}

function seksjonstekst(linje: string): string {
  const innhold = HAKE_ALENE.exec(linje)?.[1] ?? linje;
  return innhold.trim().replace(/\s*:$/, '');
}

/** Akkordene på en linje der alle ord er akkorder eller merker. Ellers null. Tall er akkorder bare i en tall-fil, på en tall-linje. */
function tolkAkkordlinje(linje: string, notasjon: Notasjon): LinjeAkkord[] | null {
  const erAkkord = notasjon === 'tall' && erTallakkordlinje(linje) ? erTallakkord : erBokstavakkord;
  const ledetekst = LEDETEKST.exec(linje);
  const start = ledetekst ? ledetekst[0].length : 0;
  const ord = Array.from(linje.slice(start).matchAll(/\S+/g), (m) => ({ token: m[0], kolonne: start + (m.index ?? 0) }));
  if (!ord.some((o) => erAkkord(o.token))) return null;
  if (!ord.every((o) => erAkkord(o.token) || MERKE.test(o.token))) return null;
  const merke = ledetekst ? [{ tekst: ledetekst[2], akkord: null, kolonne: ledetekst[1].length }] : [];
  return [
    ...merke,
    ...ord.map((o) => (erAkkord(o.token) ? lagAkkord(o.token, o.kolonne) : { tekst: o.token, akkord: null, kolonne: o.kolonne })),
  ];
}

/** Tar ut `[C]` fra teksten. Kolonnen er posisjonen i teksten uten akkorden. Alt annet i hakeparentes blir stående. */
function tolkInline(linje: string, notasjon: Notasjon): { tekst: string; akkorder: LinjeAkkord[] } {
  const erAkkord = akkordtest(notasjon);
  const akkorder: LinjeAkkord[] = [];
  let tekst = '';
  let forrige = 0;
  for (const treff of Array.from(linje.matchAll(I_HAKE))) {
    const tokens = delIOrd(treff[1]);
    if (!tokens.every(erAkkord)) continue;
    const start = treff.index ?? 0;
    tekst += linje.slice(forrige, start);
    akkorder.push(...tokens.map((token) => lagAkkord(token, tekst.length)));
    forrige = start + treff[0].length;
  }
  return { tekst: tekst + linje.slice(forrige), akkorder };
}

/** `[1]` alene i en tall-fil er en akkordlinje. */
function tallIHakeAlene(linje: string, notasjon: Notasjon): LinjeAkkord[] | null {
  const hake = HAKE_ALENE.exec(linje);
  if (notasjon !== 'tall' || !hake) return null;
  const tokens = delIOrd(hake[1]);
  return tokens.every(erTallakkord) ? tokens.map((token) => lagAkkord(token, 0)) : null;
}

function tolkRad(linje: string, notasjon: Notasjon): Rad {
  if (!linje.trim()) return { type: 'tom' };
  const akkorder = tallIHakeAlene(linje, notasjon);
  if (akkorder) return { type: 'akkorder', akkorder };
  if (erSeksjon(linje)) return { type: 'seksjon', tekst: seksjonstekst(linje) };
  const akkordlinje = tolkAkkordlinje(linje, notasjon);
  if (akkordlinje) return { type: 'akkorder', akkorder: akkordlinje };
  return { type: 'tekst', ...tolkInline(linje, notasjon) };
}

/** Sann når minst 4 linjer ikke er blanke, og hver av dem er fulgt av nøyaktig én blank linje, bortsett fra den siste. */
function harDobbelAvstand(linjer: string[]): boolean {
  const fylte = linjer.flatMap((linje, i) => (linje.trim() ? [i] : []));
  return fylte.length >= MINSTE_LINJER_DOBBEL && fylte.every((rad, i) => i === fylte.length - 1 || fylte[i + 1] === rad + 2);
}

/**
 * Parrer akkordlinjer med teksten under. Ved dobbel avstand faller de blanke linjene mellom dem bort.
 * En linje med egne inline-akkorder parres ikke, så akkordlinjen over står alene.
 */
function parr(rader: Rad[], dobbel: boolean): SangLinje[] {
  const linjer: SangLinje[] = [];
  for (let i = 0; i < rader.length; i++) {
    const rad = rader[i];
    if (rad.type !== 'akkorder') {
      linjer.push(rad);
      continue;
    }
    let j = i + 1;
    while (dobbel && rader[j]?.type === 'tom') j++;
    const under = rader[j];
    if (under?.type === 'tekst' && under.akkorder.length === 0) {
      linjer.push({ type: 'tekst', tekst: under.tekst, akkorder: rad.akkorder });
      i = j;
    } else {
      linjer.push({ type: 'tekst', tekst: '', akkorder: rad.akkorder });
    }
  }
  return linjer;
}

const erH = (rot: Rot | null): boolean => rot?.type === 'bokstav' && rot.bokstav === 'H';

function brukerH(linjer: SangLinje[]): boolean {
  return linjer.some(
    (linje) =>
      linje.type === 'tekst' && linje.akkorder.some(({ akkord }) => akkord?.type === 'akkord' && (erH(akkord.rot) || erH(akkord.bass)))
  );
}

export function tolkSang(tekst: string): SangDokument {
  const normal = tekst.normalize('NFC').replace(/\r\n?/g, '\n');
  const notasjon: Notasjon = harTallakkorder(normal) ? 'tall' : 'bokstav';
  const rader = normal.split('\n');
  const linjer = parr(rader.map((linje) => tolkRad(linje, notasjon)), harDobbelAvstand(rader));
  return { notasjon, brukerH: brukerH(linjer), linjer };
}
